// src/portal/portalUploadMachine.js
//
// THE UPLOAD FLOW'S STATES, AND — THE PART THAT MATTERS — WHERE A FAILURE
// LEAVES THE SUBMITTER.
//
// Three network calls in sequence, each able to refuse:
//
//   idle ──choose file──> inspecting ──> layers ──pick──> previewing
//                                                              │
//                                         tagging <────────────┘
//                                            │
//                                            └──submit──> submitting ──> submitted
//
// ── THE FAILURE THIS FILE EXISTS TO PREVENT ────────────────────────────────
//
// portalRefusalCopy.js gives every refusal an `action`: choose_file,
// restart_upload, reload, contact. An action naming a control THAT DOES NOT
// EXIST IN THE STATE THE ERROR LANDS IN is a dead end — the copy tells the
// submitter to pick a different file and there is no file input on screen.
//
// Three of the four are reachable by construction: `reload` is the browser's,
// `contact` is text, and `restart_upload` is rendered in every state past idle.
// `choose_file` IS THE ONE THAT CAN BREAK, because the obvious implementation
// unmounts the file input once a file has been inspected.
//
// AND IT IS NOT HYPOTHETICAL. Route 4 re-runs `prepareFeatures` (§3.15), so
// `unsupported_geometry` and `geom_invalid` — both `choose_file` — are emittable
// from SUBMIT, which lands the submitter in `tagging`, several steps past any
// file input a naive flow would still have mounted.
//
// So `ERROR_RETURNS_TO` below is a declaration the test checks against the
// RENDERED COMPONENT, not a comment: every (state, code) pair is rendered and
// the named action's control is required to be in the document.

/** @typedef {"idle"|"inspecting"|"layers"|"previewing"|"tagging"|"submitting"|"submitted"} UploadState */

export const UPLOAD_STATES = Object.freeze([
  "idle", "inspecting", "layers", "previewing", "tagging", "submitting", "submitted",
]);

/** The three states that are a request in flight, and the call each one makes. */
export const STATE_CALLS = Object.freeze({
  inspecting: "inspect",
  previewing: "preview",
  submitting: "submit",
});

/**
 * Where a failure leaves the submitter.
 *
 * NOT the state they came from in every case: a failed inspect returns to
 * `idle` because the staged upload does not exist, while a failed preview
 * returns to `layers` because the upload token is still good and another
 * sublayer may work. Getting this wrong is how a submitter ends up being told
 * to pick a layer from a list that is no longer on screen.
 */
export const ERROR_RETURNS_TO = Object.freeze({
  inspecting: "idle",
  previewing: "layers",
  submitting: "tagging",
});

/**
 * Which refusal codes each call can produce.
 *
 * ── A DECLARATION, WITH THE PARTS THAT CAN BE CHECKED, CHECKED ─────────────
 *
 * Attributing a code to a route by static scan is not reliable here: most of
 * them are returned by lib functions (`checkExt`, `prepareFeatures`, `checkTags`)
 * whose refusal objects the route spreads, so the literal is nowhere near the
 * handler. So this is declared.
 *
 * What IS checked: every code named here exists in PORTAL_REFUSAL_CODES, which
 * is itself checked against the server tree by check-doc-facts. A code invented
 * here, or renamed on the server, fails that. What is NOT checked is whether a
 * route can emit a code this file does not list for it — stated plainly rather
 * than implied, and the cost is bounded: an unlisted code still gets copy and
 * still gets an action, it is only the reachability proof below that would not
 * have covered it.
 */
export const CODES_BY_CALL = Object.freeze({
  inspect: Object.freeze([
    "no_extension", "unsupported_format", "empty_file", "file_too_large",
    "upload_form_invalid", "upload_incomplete",
    "unreadable_source", "no_layers", "too_many_layers", "crs_undeclared",
  ]),
  preview: Object.freeze([
    "token_expired", "token_scope_mismatch", "unknown_sublayer",
    "conversion_failed", "not_a_feature_collection", "crs_undeclared",
    "no_features", "unsupported_geometry", "geom_invalid",
  ]),
  submit: Object.freeze([
    "token_expired", "token_scope_mismatch", "unknown_sublayer",
    // prepareFeatures runs again at submit (§3.15), so these are reachable here
    // too — and they are the `choose_file` codes that land in `tagging`.
    "no_features", "unsupported_geometry", "geom_invalid",
    "tags_missing", "tag_incomplete", "tag_unknown_feature",
    "tag_ordinal_invalid", "tag_value_invalid", "tag_duplicate",
    "not_awaiting_submission", "review_approved", "restricted_account",
  ]),
});

/** The next state on success. */
export function advance(state) {
  switch (state) {
    case "idle": return "inspecting";
    case "inspecting": return "layers";
    case "layers": return "previewing";
    case "previewing": return "tagging";
    case "tagging": return "submitting";
    case "submitting": return "submitted";
    default: return state;
  }
}

/** Where a failure in `state` leaves the submitter. */
export function retreat(state) {
  return ERROR_RETURNS_TO[state] ?? state;
}
